import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

export const CommandStats = ({
  preset,
  startDate,
  endDate,
  onPresetChange = () => {},
  onStartDateChange = () => {},
  onEndDateChange = () => {},
}) => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const filterParams = useMemo(() => {
    const params = new URLSearchParams();
    if (preset && preset !== 'custom') {
      params.append('preset', preset);
    }
    if (preset === 'custom' && startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    }
    return params.toString();
  }, [preset, startDate, endDate]);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const baseUrl = 'https://jojomarket.ne/adminfood/functionstats.php?commandestats';
        const url = filterParams ? `${baseUrl}&${filterParams}` : baseUrl;
        const response = await axios.get(url);
        setStatsData(response.data.data);
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
        setStatsData(null);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [filterParams]);

  const filterLabel = useMemo(() => {
    switch (preset) {
      case 'today':
        return "Aujourd'hui";
      case 'yesterday':
        return 'Hier';
      case 'this_week':
        return 'Cette semaine';
      case 'this_month':
        return 'Ce mois-ci';
      case 'last7':
        return '7 derniers jours';
      case 'last30':
        return '30 derniers jours';
      case 'custom':
        if (startDate && endDate) {
          return `Du ${new Date(startDate).toLocaleDateString('fr-FR')} au ${new Date(endDate).toLocaleDateString('fr-FR')}`;
        }
        return 'Plage personnalisée';
      default:
        return '30 derniers jours';
    }
  }, [preset, startDate, endDate]);

  const refreshStats = async () => {
    setLoading(true);
    try {
      const baseUrl = 'https://jojomarket.ne/adminfood/functionstats.php?commandestats';
      const url = filterParams ? `${baseUrl}&${filterParams}` : baseUrl;
      const response = await axios.get(url);
      setStatsData(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setStatsData(null);
    } finally {
      setLoading(false);
    }
  };

  const dailyStats = statsData?.daily_stats ?? [];
  const generalStats = statsData?.general_stats ?? {};

  const chartData = useMemo(() => {
    if (!dailyStats || dailyStats.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: 'Nouvelles',
            data: [],
            borderColor: 'rgb(220, 53, 69)',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            tension: 0.4
          },
          {
            label: 'En cours',
            data: [],
            borderColor: 'rgb(255, 193, 7)',
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            tension: 0.4
          },
          {
            label: 'Livrées',
            data: [],
            borderColor: 'rgb(40, 167, 69)',
            backgroundColor: 'rgba(40, 167, 69, 0.1)',
            tension: 0.4
          }
        ]
      };
    }

    return {
      labels: dailyStats.map(stat => {
        const date = new Date(stat.date_commande);
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      }),
      datasets: [
        {
          label: 'Nouvelles',
          data: dailyStats.map(stat => stat.nouvelles),
          borderColor: 'rgb(220, 53, 69)',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          tension: 0.4
        },
        {
          label: 'En cours',
          data: dailyStats.map(stat => stat.en_cours),
          borderColor: 'rgb(255, 193, 7)',
          backgroundColor: 'rgba(255, 193, 7, 0.1)',
          tension: 0.4
        },
        {
          label: 'Livrées',
          data: dailyStats.map(stat => stat.livrees),
          borderColor: 'rgb(40, 167, 69)',
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          tension: 0.4
        }
      ]
    };
  }, [dailyStats]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!statsData) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <p className="text-muted">Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header pb-0">
        <div className="row">
          <div className="col-lg-6 col-7">
            <h6>Statistiques des Commandes ({filterLabel})</h6>
          </div>
          <div className="col-lg-6 col-5 my-auto text-end">
            <button 
              className="btn btn-sm btn-outline-primary"
              onClick={refreshStats}
            >
              <i className="fas fa-sync-alt me-1"></i>
              Actualiser
            </button>
          </div>
        </div>
        <div className="row align-items-center mt-3">
          <div className="col-md-7 mb-2 mb-md-0">
            <select
              className="form-select form-select-sm"
              value={preset}
              onChange={(e) => onPresetChange(e.target.value)}
            >
              <option value="last30">Dernières commandes (30 derniers jours)</option>
              <option value="today">Aujourd'hui</option>
              <option value="yesterday">Hier</option>
              <option value="this_week">Cette semaine</option>
              <option value="this_month">Ce mois-ci</option>
              <option value="last7">7 derniers jours</option>
              <option value="custom">Plage personnalisée</option>
            </select>
          </div>
          <div className="col-md-5">
            {preset === 'custom' && (
              <div className="d-flex flex-column flex-sm-row gap-2">
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  onChange={(e) => onStartDateChange(e.target.value)}
                />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  onChange={(e) => onEndDateChange(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="card-body">
        {/* Statistiques générales */}
        <div className="row mb-4">
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card bg-gradient-danger text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{generalStats.total_nouvelles || 0}</h4>
                    <p className="mb-0 text-sm">Nouvelles</p>
                  </div>
                  <div className="align-self-center">
                    <i className="fas fa-clock fa-2x opacity-7"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card bg-gradient-warning text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{generalStats.total_en_cours || 0}</h4>
                    <p className="mb-0 text-sm">En cours</p>
                  </div>
                  <div className="align-self-center">
                    <i className="fas fa-truck fa-2x opacity-7"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card bg-gradient-success text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{generalStats.total_livrees || 0}</h4>
                    <p className="mb-0 text-sm">Livrées</p>
                  </div>
                  <div className="align-self-center">
                    <i className="fas fa-check-circle fa-2x opacity-7"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="card bg-gradient-info text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{parseFloat(generalStats.chiffre_affaires || 0).toLocaleString('fr-FR')}</h4>
                    <p className="mb-0 text-sm">FCFA</p>
                  </div>
                  <div className="align-self-center">
                    <i className="fas fa-chart-line fa-2x opacity-7"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Graphique simple avec CSS */}
        <div className="chart-container">
          <h6 className="text-center mb-3">Évolution des commandes par jour</h6>
          <div className="chart-wrapper" style={{ height: '300px', position: 'relative' }}>
            <svg width="100%" height="100%" viewBox="0 0 800 300" className="chart-svg">
              {/* Grille */}
              <defs>
                <pattern id="grid" width="40" height="30" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 30" fill="none" stroke="#e9ecef" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
              
              {/* Axes */}
              <line x1="50" y1="250" x2="750" y2="250" stroke="#6c757d" strokeWidth="2"/>
              <line x1="50" y1="50" x2="50" y2="250" stroke="#6c757d" strokeWidth="2"/>
              
              {/* Légende */}
              <g transform="translate(600, 20)">
                <circle cx="10" cy="10" r="5" fill="rgb(220, 53, 69)"/>
                <text x="20" y="15" fontSize="12" fill="#6c757d">Nouvelles</text>
                <circle cx="10" cy="30" r="5" fill="rgb(255, 193, 7)"/>
                <text x="20" y="35" fontSize="12" fill="#6c757d">En cours</text>
                <circle cx="10" cy="50" r="5" fill="rgb(40, 167, 69)"/>
                <text x="20" y="55" fontSize="12" fill="#6c757d">Livrées</text>
              </g>
              
              {/* Courbes */}
              {chartData.datasets.map((dataset, datasetIndex) => {
                if (!dataset.data || dataset.data.length === 0) {
                  return null;
                }

                const maxValue = Math.max(...dataset.data);
                const safeMax = maxValue > 0 ? maxValue : 1;
                const stepCount = dataset.data.length > 1 ? dataset.data.length - 1 : 1;

                const points = dataset.data.map((value, index) => {
                  const x = 50 + (index * (700 / stepCount));
                  const y = 250 - ((value || 0) * 200 / safeMax);
                  return `${x},${y}`;
                }).join(' ');
                
                return (
                  <g key={datasetIndex}>
                    <polyline
                      points={points}
                      fill="none"
                      stroke={dataset.borderColor}
                      strokeWidth="3"
                    />
                    {dataset.data.map((value, index) => {
                      const x = 50 + (index * (700 / stepCount));
                      const y = 250 - ((value || 0) * 200 / safeMax);
                      return (
                        <circle
                          key={index}
                          cx={x}
                          cy={y}
                          r="4"
                          fill={dataset.borderColor}
                        />
                      );
                    })}
                  </g>
                );
              })}
              
              {/* Labels des axes */}
              {chartData.labels.map((label, index) => {
                const stepCount = chartData.labels.length > 1 ? chartData.labels.length - 1 : 1;
                const x = 50 + (index * (700 / stepCount));
                return (
                  <text
                    key={index}
                    x={x}
                    y="270"
                    textAnchor="middle"
                    fontSize="10"
                    fill="#6c757d"
                  >
                    {label}
                  </text>
                );
              })}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandStats;
